
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Edit2, Trash2, MessageSquare, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useChatSessions } from '@/hooks/useChatSessions';
import { cn } from '@/lib/utils';

interface SessionSidebarProps {
  organisationId?: string;
  currentSessionId?: string;
  onSessionSelect: (sessionId: string) => void;
  onNewSession: (sessionId: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  hideHeader?: boolean;
}

const SessionSidebar: React.FC<SessionSidebarProps> = ({
  organisationId,
  currentSessionId,
  onSessionSelect,
  onNewSession,
  isCollapsed = false,
  onToggleCollapse,
  hideHeader = false
}) => {
  const { sessions, isLoading, createSession, renameSession, deleteSession } = useChatSessions(organisationId);
  const [newSessionName, setNewSessionName] = useState('');
  const [editingSession, setEditingSession] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) return;
    
    const sessionId = await createSession(newSessionName.trim(), organisationId);
    if (sessionId) {
      onNewSession(sessionId);
      setNewSessionName('');
      setIsCreatingSession(false);
    }
  };

  const handleRenameSession = async (sessionId: string) => {
    if (!editName.trim()) return;
    
    await renameSession(sessionId, editName.trim());
    setEditingSession(null);
    setEditName('');
  };

  const handleDeleteSession = async (sessionId: string) => {
    await deleteSession(sessionId);
    if (currentSessionId === sessionId) {
      // Create a new session and switch to it
      const newSessionId = await createSession('New Chat', organisationId);
      if (newSessionId) {
        onNewSession(newSessionId);
      } else {
        // If we can't create a new session, at least clear the current one
        onSessionSelect('');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredSessions = sessions.filter(session =>
    session.session_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isCollapsed) {
    return (
      <div data-testid="sessions-sidebar" className="w-14 border-r bg-gray-50/40 flex flex-col">
        {/* Header with collapse toggle */}
        <div className="p-2 border-b bg-white flex justify-center">
          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              className="h-8 w-8 p-0 hover:bg-gray-200"
              title="Expand sidebar"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* New session button */}
        <div className="p-2">
          <Button
            size="sm"
            className="w-full h-8 p-0"
            onClick={() => setIsCreatingSession(true)}
            title="New Session"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Sessions list */}
        <ScrollArea className="flex-1 h-0">
          <div className="p-1 space-y-1">
            {sessions.slice(0, 10).map((session) => (
              <Button
                key={session.id}
                variant={currentSessionId === session.session_id ? "secondary" : "ghost"}
                size="sm"
                className="w-full h-8 p-0"
                onClick={() => onSessionSelect(session.session_id)
                }
                title={session.session_name}
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            ))}
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div data-testid="sessions-sidebar" className="w-full md:w-64 border-r bg-gray-50/40 flex flex-col h-full min-h-0">
      {/* Header - hidden on mobile when hideHeader is true */}
      {!hideHeader && (
        <div className="flex-shrink-0 p-4 border-b bg-white">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-sm">Chat Sessions</h2>
            <div className="flex items-center gap-1">
              <Button
                data-testid="new-session"
                size="sm"
                onClick={() => setIsCreatingSession(true)}
                className="h-7 px-2"
              >
                <Plus className="h-3 w-3 mr-1" />
                New
              </Button>
              {onToggleCollapse && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleCollapse}
                  className="h-7 w-7 p-0 hover:bg-gray-200"
                  title="Collapse sidebar"
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
            <Input
              placeholder="Search sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-7 h-7 text-xs"
            />
          </div>
        </div>
      )}
      
      {/* Search bar when header is hidden (mobile) */}
      {hideHeader && (
        <div className="flex-shrink-0 p-3 border-b bg-white">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
            <Input
              placeholder="Search sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-7 h-8 text-sm"
            />
          </div>
        </div>
      )}

      {/* New Session Creation */}
      {isCreatingSession && (
        <div className="p-3 border-b bg-blue-50">
          <div className="flex gap-2">
            <Input
              placeholder="Session name..."
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateSession();
                } else if (e.key === 'Escape') {
                  setIsCreatingSession(false);
                  setNewSessionName('');
                }
              }}
              className="h-7 text-xs"
              autoFocus
            />
            <Button size="sm" onClick={handleCreateSession} className="h-7 px-2">
              ✓
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => {
                setIsCreatingSession(false);
                setNewSessionName('');
              }}
              className="h-7 px-2"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Sessions List */}
      <ScrollArea className="flex-1 min-h-0 h-0">
        <div className="p-2 space-y-1">
          {isLoading ? (
            <div className="text-center text-gray-500 py-8 text-sm">Loading sessions...</div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center text-gray-500 py-8 text-sm">
              {searchTerm ? 'No sessions found' : 'No sessions yet'}
            </div>
          ) : (
            filteredSessions.map((session) => (
              <div
                key={session.id}
                className={cn(
                  "group relative p-2 rounded-lg cursor-pointer transition-colors text-sm",
                  currentSessionId === session.session_id
                    ? 'bg-blue-100 border border-blue-200'
                    : 'hover:bg-gray-100'
                )}
                onClick={() => onSessionSelect(session.session_id)
                }
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 pr-2">
                    {editingSession === session.session_id ? (
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleRenameSession(session.session_id);
                          } else if (e.key === 'Escape') {
                            setEditingSession(null);
                            setEditName('');
                          }
                        }}
                        onBlur={() => handleRenameSession(session.session_id)}
                        className="h-5 text-xs p-1"
                        autoFocus
                      />
                    ) : (
                      <h4 className="font-medium text-xs truncate leading-4">
                        {session.session_name}
                      </h4>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                      <span>{session.message_count || 0} msgs</span>
                      <span>•</span>
                      <span>{formatDate(session.last_message_at || session.created_at)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingSession(session.session_id);
                        setEditName(session.session_name);
                      }}
                    >
                      <Edit2 className="h-2.5 w-2.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Chat Session</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{session.session_name}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSession(session.session_id);
                            }}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default SessionSidebar;
